<template>
  <b-card no-body>
    <b-card-header>
      <!-- title -->
      <b-card-title>{{ $t(i18nTitleVariable) }} <span :class="complianceColor" style="font-weight: 600">{{ compliance }}</span></b-card-title>
      <b-card-sub-title class="mt-0">{{ $t(i18nSubTitleVariable) }}</b-card-sub-title>
    </b-card-header>

    <b-card-body>
      <vue-apex-charts
        type="heatmap"
        :height="this.height"
        :options="heatMapChart.options"
        :series="this.series"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BCardTitle, BCardSubTitle, BDropdown, BDropdownItem } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import { EventBus } from "@/views/habit/EventBus.js"
import i18n from '@/libs/i18n'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    VueApexCharts,
    BCardTitle,
    BCardSubTitle,
    BDropdown,
    BDropdownItem,
  },
  props: {
    dialoguesPerDayData: {
      type: Array,
      required: true,
    },
    i18nTitleVariable: {
      type: String,
      default: "dialoguesPerDay",
    },
    i18nSubTitleVariable: {
      type: String,
      default: "",
    },
    compliance: {
      type: String,
      default: "",
    },
    complianceColor: {
      type: String,
      default: "text-warning",
    },
    defaultLanguage: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      heatMapChart: {
        options: {
          tooltip: {
            followCursor: false,
            custom: function({ series, seriesIndex, dataPointIndex, w }) {
              const { images, mood, improvements, recognitions, realValue } = w.config.series[seriesIndex].data[dataPointIndex]
              return (
                `
                  <div style="padding: 10px">
                    <div>
                      <span>${w.globals.seriesNames[seriesIndex]} - ${w.globals.labels[dataPointIndex]}</span>
                    </div>
                    <div>
                      <span>${i18n.t('tooltip.dialogues')}: </span>
                      <span style="font-weight: bold">${realValue}</span>
                    </div>
                    <div>
                      <span>${i18n.t('tooltip.images')}: </span>
                      <span style="font-weight: bold">${images}</span>
                    </div>
                    <div>
                      <span>${i18n.t('tooltip.mood_answers')}: </span>
                      <span style="font-weight: bold">${mood}</span>
                    </div>
                    <div>
                      <span>${i18n.t('tooltip.improvements')}: </span>
                      <span style="font-weight: bold">${improvements}</span>
                    </div>
                    <div>
                      <span>${i18n.t('tooltip.recognitions')}: </span>
                      <span style="font-weight: bold">${recognitions}</span>
                    </div>
                  </div>
                `
              )
            }
          },
          dataLabels: {
            enabled: true,
            style: {
              colors: ['#ffffff']
            },
            formatter: function(val, opts) {
              return opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].realValue
            },
          },
          legend: {
            show: false,
          },
          stroke: {
            width: 1,
            colors: ['#d3d3d3']
          },
          plotOptions: {
            heatmap: {
              enableShades: false,
              colorScale: {
                ranges: [
                  { from: 0, to: 0, color: '#ffffff' },   // No tuvo diálogos
                  { from: 1, to: 1, color: $themeColors.warning },    // Tuvo diálogos pero no llegó al esperado
                  { from: 2, to: 2, color: $themeColors.success },    // Llegó o superó el número esperado de diálogos
                ],
              },
            },
          },
          xaxis: {
            labels: {
              show: true,
              formatter: this.formatDateWithWeekday,
            },
            axisBorder: {
              show: true,
            },
            axisTicks: {
              show: true,
            },
          },
          chart: {
            height: 350,
            type: 'heatmap',
            toolbar: {
              show: false,
            },
            events: {
              click: function(event, chartContext, config) {
                if (config.seriesIndex < 0 || config.dataPointIndex < 0) return
                const locationId = config.config.series[config.seriesIndex].locationId
                const date = config.globals.labels[config.dataPointIndex]
                EventBus.$emit("filterByLocation", locationId)
                EventBus.$emit("filterByDate", date)
              },
              dataPointMouseEnter: function(event) {
                event.srcElement.style.cursor = "pointer"
              }
            }
          },
        },
      },
    }
  },
  computed: {
    height() {
      return 120 + 20 * this.dialoguesPerDayData.length
    },
    series() {
      return this.dialoguesPerDayData.slice(0, 50).map(location => {
        const expected = location.dailyDialoguesCount

        const newData = location.data.map(point => {
          let normalizedValue = 0

          if (point.y === 0) {
            normalizedValue = 0
          } 
          else if (point.y >= expected) {
            normalizedValue = 2
          } 
          else {
            normalizedValue = 1
          }

          return {
            ...point,
            realValue: point.y,   // guardamos el valor real
            y: normalizedValue,   // este controla el color
          }
        })

        return {
          ...location,
          data: newData,
        }
      })
    }
  },
  methods: {
    formatDateWithWeekday(value) {
      // value viene en formato como "1/1/2026", "2/1/2026", etc.
      const dateParts = value.split('/')
      const day = parseInt(dateParts[this.defaultLanguage === 'es' ? 0 : 1])
      const month = parseInt(dateParts[this.defaultLanguage === 'es' ? 1 : 0]) - 1 // Meses en JS son 0-indexed
      const year = parseInt(dateParts[2])
      
      const date = new Date(year, month, day)
      const dayOfWeek = date.getDay()
      
      // Array con las letras de los días (0=Domingo, 1=Lunes, etc.)
      const dayLetters = this.defaultLanguage === 'es'
        ? ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S']
        : ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa']
      
      return `${value} (${dayLetters[dayOfWeek]})`
    }
  }
}
</script>